import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { isPdfSource, PdfViewer } from './PdfViewer';
import { cn } from '../ui/utils';

export const getUploadedFileKind = (name, mimeType) => {
  const fileName = String(name || '');
  const type = String(mimeType || '');

  if (isPdfSource(fileName, type)) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)($|\?)/i.test(fileName) || /^image\//i.test(type)) {
    return 'image';
  }
  if (/\.(txt|text|md|csv|log)($|\?)/i.test(fileName) || /^text\//i.test(type)) {
    return 'text';
  }
  if (
    /\.docx($|\?)/i.test(fileName) ||
    /officedocument\.wordprocessingml/i.test(type)
  ) {
    return 'docx';
  }
  if (/\.doc($|\?)/i.test(fileName) || type === 'application/msword') {
    return 'doc';
  }
  return 'other';
};

export const isWordDocument = (name, mimeType) => {
  const kind = getUploadedFileKind(name, mimeType);
  return kind === 'docx' || kind === 'doc';
};

const TextViewer = ({ fileUrl }) => {
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const renderText = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error('Could not load file');
        const value = await response.text();
        if (!cancelled) setText(value);
      } catch {
        if (!cancelled) setErrorMessage('Could not preview this text file.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    renderText();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        Loading file...
      </div>
    );
  }

  if (errorMessage) {
    return <p className="px-4 py-8 text-center text-sm text-muted-foreground">{errorMessage}</p>;
  }

  return (
    <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap break-words bg-white px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
      {text || 'This file is empty.'}
    </pre>
  );
};

export function UploadedFileViewer({ fileUrl, fileName, mimeType, className }) {
  if (!fileUrl) return null;

  const kind = getUploadedFileKind(fileName, mimeType);

  if (kind === 'pdf') {
    return <PdfViewer fileUrl={fileUrl} variant="light" className={className} />;
  }

  if (kind === 'image') {
    return (
      <div className={cn('overflow-hidden rounded-lg border border-border bg-muted/30', className)}>
        <img
          src={fileUrl}
          alt={fileName || 'Uploaded image'}
          className="mx-auto max-h-[480px] w-full object-contain"
        />
      </div>
    );
  }

  if (kind === 'text') {
    return (
      <div className={cn('overflow-hidden rounded-lg border border-border bg-muted/30', className)}>
        <TextViewer fileUrl={fileUrl} />
      </div>
    );
  }

  if (kind === 'docx' || kind === 'doc') {
    return null;
  }

  return (
    <p className="px-4 py-6 text-center text-sm text-muted-foreground">
      Preview is not available for this file type. You can download it instead.
    </p>
  );
}
